/**
 * React Query-based Draft Management Hook
 * Provides automatic deduplication, caching, and optimistic updates for drafts
 */

import React, { useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { draftService, SaveDraftParams } from '@/services/draftService';
import { toast } from '@/hooks/use-toast';
import { debounce } from 'lodash';
import { useDraftConflictDetection } from '@/hooks/drafts/useDraftConflictDetection';
import { DraftConflictDialog } from '@/components/drafts/DraftConflictDialog';
import type { DraftLoadResponse } from '@/types/drafts';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 500; // 500ms

export interface UseDraftQueryOptions {
  formSlug: string;
  step?: string | null;
  enabled?: boolean;
  onHydrate?: (data: DraftLoadResponse) => void;
}

export interface UseDraftQueryReturn {
  draft: DraftLoadResponse | null | undefined;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  saveDraft: (answers: Record<string, any>) => void;
  saveDraftImmediate: (answers: Record<string, any>) => Promise<void>;
  clearDraft: () => Promise<void>;
  refetch: () => void;
  ConflictDialog: React.ReactNode;
}

/**
 * Draft query keys factory
 */
export const draftKeys = {
  all: ['drafts'] as const,
  user: (userId: string) => [...draftKeys.all, userId] as const,
  detail: (userId: string, formSlug: string) => [...draftKeys.user(userId), formSlug] as const,
};

export function useDraftQuery(options: UseDraftQueryOptions): UseDraftQueryReturn {
  const { formSlug, step = null, enabled = true, onHydrate } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const lastSavedRef = useRef<Date | null>(null);
  const hasHydratedRef = useRef(false);
  const localAnswersRef = useRef<Record<string, any>>({});

  const userId = user?.id;

  // Conflict detection
  const {
    conflict,
    isConflictDialogOpen,
    resolveConflict,
    dismissConflict,
  } = useDraftConflictDetection({
    onResolve: (resolvedAnswers) => {
      // Update local answers with resolved values
      localAnswersRef.current = resolvedAnswers;
      // Trigger immediate save
      if (userId) {
        saveMutation.mutate({
          userId,
          formSlug,
          step,
          answers: resolvedAnswers,
        });
      }
    },
  });

  // Query for fetching draft
  const {
    data: draft,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: draftKeys.detail(userId || '', formSlug),
    queryFn: () => draftService.getDraft(userId!, formSlug),
    enabled: enabled && !!userId,
    staleTime: STALE_TIME,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Call onHydrate when draft is first loaded
  useMemo(() => {
    if (draft && !hasHydratedRef.current && onHydrate) {
      hasHydratedRef.current = true;
      onHydrate(draft);
    }
  }, [draft, onHydrate]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (params: SaveDraftParams) => draftService.saveDraft(params),
    onSuccess: () => {
      lastSavedRef.current = new Date();
      // Invalidate to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: draftKeys.detail(userId || '', formSlug),
      });
      
      toast({
        title: 'Autosaved',
        description: lastSavedRef.current.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        }),
        duration: 2000,
      });
    },
    onError: (error) => {
      console.error('Failed to save draft:', error);
      toast({
        title: 'Autosave failed',
        description: "Your changes weren't saved automatically.",
        variant: 'destructive',
        duration: 3000,
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () =>
      draftService.deleteDraft({
        userId: userId!,
        formSlug,
        step,
      }),
    onSuccess: () => {
      lastSavedRef.current = null;
      hasHydratedRef.current = false;
      localAnswersRef.current = {};
      // Invalidate all user drafts
      queryClient.invalidateQueries({
        queryKey: draftKeys.user(userId || ''),
      });
      
      toast({
        title: 'Draft cleared',
        description: 'Your saved data has been removed.',
        duration: 3000,
      });
    },
    onError: (error) => {
      console.error('Failed to delete draft:', error);
      toast({
        title: 'Failed to clear draft',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    },
  });

  // Debounced save function
  const debouncedSave = useMemo(
    () =>
      debounce((answers: Record<string, any>) => {
        if (!userId) return;
        
        // Store local answers for conflict detection
        localAnswersRef.current = answers;
        
        saveMutation.mutate({
          userId,
          formSlug,
          step,
          answers,
        });
      }, DEBOUNCE_DELAY),
    [userId, formSlug, step, saveMutation]
  );

  // Save draft (debounced)
  const saveDraft = useCallback(
    (answers: Record<string, any>) => {
      if (!userId) return;
      debouncedSave(answers);
    },
    [userId, debouncedSave]
  );

  // Save draft immediately (no debounce)
  const saveDraftImmediate = useCallback(
    async (answers: Record<string, any>) => {
      if (!userId) return;
      
      localAnswersRef.current = answers;
      
      await saveMutation.mutateAsync({
        userId,
        formSlug,
        step,
        answers,
      });
    },
    [userId, formSlug, step, saveMutation]
  );

  // Clear draft
  const clearDraft = useCallback(async () => {
    if (!userId) return;
    await deleteMutation.mutateAsync();
  }, [userId, deleteMutation]);

  // Render conflict dialog
  const ConflictDialog = React.createElement(DraftConflictDialog, {
    conflict,
    isOpen: isConflictDialogOpen,
    onResolve: resolveConflict,
    onDismiss: dismissConflict,
  });

  return {
    draft,
    isLoading,
    isSaving: saveMutation.isPending,
    lastSaved: lastSavedRef.current,
    saveDraft,
    saveDraftImmediate,
    clearDraft,
    refetch: () => refetch(),
    ConflictDialog,
  };
}
